/** @file boneMarks: a wire bone per shown node from its parent joint to it, a circle when bold, a root ring. */
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import type { Scene } from '@babylonjs/core/scene.js';
import { POSE_LAYOUT as L } from '@zilo/pose3d-pose/layout/poseLayout';
import { boneLook, type BoneLook } from './boneLook';
import { boneMeshes } from './boneMeshes';
import { BONE_SHAPE as S } from './boneShape';
import { boneTails } from './boneTails';
import { crossTo } from './crossTo';
import type { MarksInput } from './jointMarks';
import { jointShown } from './jointShown';
import type { V3 } from './matrixRows';
import { pictureWorldPoint } from './pictureWorldPoint';
import { segmentQuad } from './segmentQuad';
import { VIEWPORT_COLORS } from './viewportColors';

/** `update(input)` rewrites the segments; `shown()` is how many bones are drawn, `segments()` how many quads. */
export type BoneMarks = { update(input: MarksInput | null): void; shown(): number; segments(): number };

/** One frame's segment matrices and colours; the tails and their scratch. */
type Buffers = { seg: Float32Array; color: Float32Array; tails: Float64Array; counts: Float64Array };

const YELLOW = Color3.FromHexString(VIEWPORT_COLORS.hover);
/** The root's ground ring: yellow wire at the bold width. */
const ROOT: BoneLook = { edge: [YELLOW.r, YELLOW.g, YELLOW.b, 0.9], width: S.boldLine };
/** Segments per node (12 bone edges and the head circle). */
const PER_NODE = S.edges.length + S.joint.segments;
const AXES = [0, 1, 2] as const;

/** Scratch (nothing is allocated per frame): head, bone, sides, view right/up/back (Babylon frame), segment ends. */
const H: V3 = [0, 0, 0];
const D: V3 = [0, 0, 0];
const A: V3 = [0, 0, 0];
const C: V3 = [0, 0, 0];
const R: V3 = [0, 0, 0];
const U: V3 = [0, 0, 0];
const B: V3 = [0, 0, 0];
const P: V3 = [0, 0, 0];
const Q: V3 = [0, 0, 0];
const VERTS = new Float64Array(18);
/** The circle being written: its centre, two in-plane unit vectors, radius (scene units) and segment count. */
const CIRCLE: { centre: V3; e1: V3; e2: V3; r: number; n: number } = { centre: [0, 0, 0], e1: R, e2: U, r: 0, n: 0 };
/** Ground-plane unit vectors (Babylon x and z). */
const GROUND_X: V3 = [1, 0, 0];
const GROUND_Z: V3 = [0, 0, 1];

/** Segment `k` from `P` to `Q`: a quad `look.width` CSS px wide facing the camera, in `look`'s colour. */
function segment(b: Buffers, k: number, look: BoneLook, px: number): void {
  segmentQuad(b.seg, 16 * k, P, Q, B, look.width * px);
  b.color.set(look.edge, 4 * k);
}

/** The segments of `CIRCLE` from index `k`; returns the next free index. */
function circle(b: Buffers, k: number, look: BoneLook, px: number): number {
  const { centre, e1, e2, r, n } = CIRCLE;
  for (let s = 0; s < n; s++) {
    const t0 = (2 * Math.PI * s) / n;
    const t1 = (2 * Math.PI * (s + 1)) / n;
    for (const c of AXES) {
      P[c] = centre[c] + r * (Math.cos(t0) * e1[c] + Math.sin(t0) * e2[c]);
      Q[c] = centre[c] + r * (Math.cos(t1) * e1[c] + Math.sin(t1) * e2[c]);
    }
    segment(b, k + s, look, px);
  }
  return k + n;
}

/** Scratch: a world point (z up) before and after `pictureWorldPoint`. */
const W: V3 = [0, 0, 0];
const S2: V3 = [0, 0, 0];

/** Node `i`'s pivot fraction on axis `k` (`MarksInput.fracs`; 0.5 without them). */
const fracOf = (m: MarksInput, i: number, k: 0 | 1): number => m.fracs?.[2 * i + k] ?? 0.5;

/** World point `W` where the picture shows it (`pictureWorldPoint` with node `j`'s fractions) into `out`, Babylon display axes. */
function shown(out: V3, m: MarksInput, j: number): void {
  pictureWorldPoint(S2, W, m.view, fracOf(m, j, 0), fracOf(m, j, 1));
  out[0] = S2[0]; out[1] = S2[2] * m.view.zScaleView; out[2] = S2[1];
}

/**
 * Head and bone vector of node `i` in Babylon display coordinates, into `H` and `D`: the bone a key on `i` turns,
 * from the parent joint (the pose's origin columns) to the node; a root, which has no such bone, shows its stub tail.
 * Both ends are placed as the picture places its point A (`pictureWorldPoint`: rounded, projected, snapped with the
 * fraction of the part drawn on that joint), so the bone passes through the drawn pivot pixel.
 */
function headAndBone(m: MarksInput, t: Float64Array, i: number): void {
  const o = i * L.stride;
  const at = (c: number): number => m.pose[o + c] ?? 0;
  const tail = (c: number): number => t[3 * i + c] ?? 0;
  const parent = at(L.parentIndex);
  const root = parent < 0;
  // head: the parent joint (origin columns), or the node itself for a root; far end: the node, or the root's stub tail
  W[0] = at(root ? L.px : L.ox); W[1] = at(root ? L.py : L.oy); W[2] = at(root ? L.pz : L.oz);
  shown(H, m, root ? i : parent);
  W[0] = root ? tail(0) : at(L.px); W[1] = root ? tail(1) : at(L.py); W[2] = root ? tail(2) : at(L.pz);
  shown(D, m, i);
  for (const c of AXES) D[c] -= H[c];
}

/** The six octahedron vertices (head, the ring at `ringAt` along A, C, -A, -C, the tail) into `VERTS`. */
function vertices(): void {
  for (const c of AXES) {
    const ring = H[c] + S.ringAt * D[c];
    VERTS[c] = H[c]; VERTS[3 + c] = ring + A[c]; VERTS[6 + c] = ring + C[c]; VERTS[9 + c] = ring - A[c]; VERTS[12 + c] = ring - C[c];
    VERTS[15 + c] = H[c] + D[c];
  }
}

/** Node `i`'s 12 bone edges and its screen-facing head circle from segment `k`; returns the next free index. */
function bone(b: Buffers, k: number, m: MarksInput, i: number): number {
  headAndBone(m, b.tails, i);
  const w = Math.min(S.widthMax * m.px, Math.max(S.widthMin * m.px, S.widthShare * Math.hypot(D[0], D[1], D[2])));
  crossTo(A, D, B, w);
  crossTo(C, D, A, w);
  vertices();
  const look = boneLook(m, i);
  for (let e = 0; e < S.edges.length; e++) {
    const pair = S.edges[e] ?? S.edges[0];
    for (const c of AXES) { P[c] = VERTS[3 * pair[0] + c] ?? 0; Q[c] = VERTS[3 * pair[1] + c] ?? 0; }
    segment(b, k + e, look, m.px);
  }
  for (const c of AXES) CIRCLE.centre[c] = H[c] + D[c]; // the circle marks the node, at the bone's far end
  // the joint circle only on bold bones (selected subtree, hover): a plain skeleton is crystals alone
  CIRCLE.e1 = R; CIRCLE.e2 = U; CIRCLE.r = (look.width > S.line ? S.joint.radius : 0) * m.px; CIRCLE.n = S.joint.segments;
  return circle(b, k + S.edges.length, look, m.px);
}

/** The view's right, up and back in Babylon coordinates (y/z swapped) into `R`, `U`, `B`. */
function viewAxes(m: MarksInput): void {
  const { right, up, back } = m.view.basis;
  R[0] = right[0]; R[1] = right[2]; R[2] = right[1];
  U[0] = up[0]; U[1] = up[2]; U[2] = up[1];
  B[0] = back[0]; B[1] = back[2]; B[2] = back[1];
}

/** The entity origin's yellow ground ring from segment 0 (centred where the picture shows the origin); returns the next free index. */
function rootRing(b: Buffers, m: MarksInput): number {
  W.fill(0);
  shown(CIRCLE.centre, m, -1);
  CIRCLE.e1 = GROUND_X; CIRCLE.e2 = GROUND_Z; CIRCLE.r = S.root.radius * m.px; CIRCLE.n = S.root.segments;
  return circle(b, 0, ROOT, m.px);
}

/** Fresh buffers for `n` nodes, handed to the mesh (only when the node count grows). */
function allocate(mesh: Mesh, n: number): Buffers {
  const segs = PER_NODE * n + S.root.segments;
  const b: Buffers = { seg: new Float32Array(16 * segs), color: new Float32Array(4 * segs), tails: new Float64Array(3 * n), counts: new Float64Array(n) };
  mesh.thinInstanceSetBuffer('matrix', b.seg, 16, false);
  mesh.thinInstanceSetBuffer('color', b.color, 4, false);
  return b;
}

/**
 * Wire bones in the overlay's marks group at full resolution, always on top: per shown node (`jointShown`) the 12
 * edges of its octahedron (tail from `boneTails`) and a screen-facing head circle, coloured by `boneLook`; the entity
 * origin gets a yellow ground ring. One thin-instanced quad mesh (one draw call); buffers grow with the node count,
 * nothing else is created per frame. Bones pointing at the camera foreshorten as projected (no length clamp).
 */
export function boneMarks(scene: Scene): BoneMarks {
  const mesh = boneMeshes(scene);
  let buf: Buffers | null = null;
  let bones = 0;
  let segs = 0;
  return {
    update: (m) => {
      const n = m === null ? 0 : m.names.length;
      mesh.setEnabled(n > 0);
      if (m === null || n === 0) return;
      if (buf === null || buf.counts.length < n) buf = allocate(mesh, n);
      boneTails(m.pose, n, buf.tails, buf.counts, m.leafLen);
      viewAxes(m);
      bones = 0;
      segs = rootRing(buf, m);
      for (let i = 0; i < n; i++) if (jointShown(m, i)) { segs = bone(buf, segs, m, i); bones++; }
      mesh.thinInstanceCount = segs;
      mesh.thinInstanceBufferUpdated('matrix');
      mesh.thinInstanceBufferUpdated('color');
    },
    shown: () => bones,
    segments: () => segs,
  };
}
